import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * {@link https://ngrx.io/docs State} of this feature
 */
export interface State {
  error?: Error
  /**
   * Employees to make responsible for every selected record. `undefined`
   * while no bulk update is in progress.
   */
  employeeIds?: readonly ents.extra.space.Employee.Id[]
  /** Employee, out of {@link State.employeeIds}, to make the primary */
  primaryEmployeeId?: ents.extra.space.Employee.Id
  isSubmitting: boolean
}

export namespace State {
  export const INITIAL: Readonly<State> = {
    employeeIds: undefined,
    error: undefined,
    isSubmitting: false,
    primaryEmployeeId: undefined,
  }

  /** Whether a bulk responsible persons update is in progress */
  export function isUpdating(state: State): boolean {
    return TypeChecker.isNotNullish(state.employeeIds)
  }

  /**
   * Opens a bulk responsible persons update for the current selection.
   *
   * Nothing is prefilled: the selected records may hold different responsible
   * persons, and there are none the modal could honestly show.
   */
  export function onInitiate(): State {
    return { ...INITIAL, employeeIds: [] }
  }

  /**
   * Adds or removes one employee.
   *
   * Dropping the employee who was going to be the primary drops the primary
   * with them, so that one is never submitted for someone not being added.
   */
  export function onToggleEmployees(
    { employeeIds: toggledEmployeeIds, selected }: State.ToggleEmployees,
    state: State,
  ): State {
    if (!isUpdating(state)) {
      return state
    }

    const currentEmployeeIds = state.employeeIds ?? []
    const employeeIds = selected
      ? [
          ...currentEmployeeIds,
          ...toggledEmployeeIds.filter(
            employeeId => !currentEmployeeIds.includes(employeeId),
          ),
        ]
      : currentEmployeeIds.filter(
          employeeId => !toggledEmployeeIds.includes(employeeId),
        )

    const { primaryEmployeeId } = state

    return {
      ...state,
      employeeIds,
      primaryEmployeeId:
        TypeChecker.isNotNullish(primaryEmployeeId) &&
        employeeIds.includes(primaryEmployeeId)
          ? primaryEmployeeId
          : undefined,
    }
  }

  /** Stores the employee to make the primary */
  export function onSetPrimaryEmployee(
    primaryEmployeeId: ents.extra.space.Employee.Id | undefined,
    state: State,
  ): State {
    return isUpdating(state) ? { ...state, primaryEmployeeId } : state
  }

  /** Marks a bulk responsible persons request as in flight */
  export function onRequest(state: State): State {
    return { ...state, error: undefined, isSubmitting: true }
  }

  /** Records a failed bulk responsible persons request */
  export function onError(error: Error, state: State): State {
    return { ...state, error, isSubmitting: false }
  }

  /** Clears the in flight marker for a request that never ran */
  export function onCancellation(state: State): State {
    return { ...state, isSubmitting: false }
  }

  /** Employees being added to, or removed from, the bulk update */
  export interface ToggleEmployees {
    readonly employeeIds: readonly ents.extra.space.Employee.Id[]
    readonly selected: boolean
  }
}
